import React, { useId } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import './PhaseActivity.css';
import './chat.css';
import {
  SHAPES, SILHOUETTE, FISSURE, GYRUS_UL, GYRUS_UR, GYRUS_LL, GYRUS_LR,
} from './brainPaths';

/**
 * Ephemeral "this is going to take a moment" card, shown while a long phase
 * runs and gone as soon as it finishes (it is rendered from live phase state,
 * never persisted as a chat block).
 *
 * Preprocessing and training are the only phases slow enough to warrant it —
 * everything else returns fast enough that the plain Thinking bubble reads
 * better than an illustration.
 */

/* ------------------------------------------------------------------ table -- */

// Cell size and pitch. PhaseActivity.css moves cells by percentages of
// these (pitch / size), so the two must change together.
const COL_X = (i) => 8 + i * 44;
const ROW_Y = [26, 44, 62, 80];
const CELL_W = 40;
const CELL_H = 14;
const TABLE_W = 232;
const TABLE_H = 104;

const Cell = ({ x, y, className, opacity = 1, solid }) => (
  <rect
    className={className}
    x={x} y={y} width={CELL_W} height={CELL_H} rx={3}
    fill={solid ? '#fff' : 'currentColor'}
    opacity={solid ? 1 : opacity}
  />
);

// A column of header + data cells. `dim` varies per column so the table reads
// as data rather than a uniform grid.
const Column = ({ i, className, dim, solid }) => (
  <g className={className}>
    <rect
      x={COL_X(i)} y={8} width={CELL_W} height={10} rx={3}
      fill={solid ? '#fff' : 'currentColor'} opacity={solid ? 1 : 0.75}
    />
    {ROW_Y.map((y, r) => (
      <Cell key={y} x={COL_X(i)} y={y} opacity={dim[r]} solid={solid} />
    ))}
  </g>
);

/**
 * The table itself. Rendered twice — once visibly, once (with `solid`) as the
 * shimmer's mask — so the highlight lands on the cells and nowhere else. Both
 * copies carry the same animation classes, so they can never drift apart.
 */
const TableContent = ({ solid }) => (
  <>
    <Column i={0} dim={[0.5, 0.3, 0.44, 0.24]} solid={solid} />

    {/* Two adjacent cells that trade places. Adjacent so neither crosses a
        static cell on the way; different shades so the swap shows once it
        settles. The brighter one is drawn last, so it passes over the other. */}
    <g>
      <rect
        x={COL_X(1)} y={8} width={CELL_W} height={10} rx={3}
        fill={solid ? '#fff' : 'currentColor'} opacity={solid ? 1 : 0.75}
      />
      <Cell x={COL_X(1)} y={ROW_Y[0]} opacity={0.4} solid={solid} />
      <Cell className="q-pa-cell-b" x={COL_X(1)} y={ROW_Y[2]} opacity={0.26} solid={solid} />
      <Cell className="q-pa-cell-a" x={COL_X(1)} y={ROW_Y[1]} opacity={0.62} solid={solid} />
      <Cell x={COL_X(1)} y={ROW_Y[3]} opacity={0.32} solid={solid} />
    </g>

    {/* Dropped column — collapses horizontally about its own centre. */}
    <Column i={2} className="q-pa-col-drop" dim={[0.36, 0.5, 0.28, 0.4]} solid={solid} />

    {/* Slides left to close the gap the dropped column leaves. */}
    <Column i={3} className="q-pa-col-shift" dim={[0.46, 0.26, 0.5, 0.32]} solid={solid} />

    {/* The engineered column that appears afterwards. */}
    <Column i={4} className="q-pa-col-add" dim={[0.5, 0.38, 0.3, 0.46]} solid={solid} />
  </>
);

function TableAnimation() {
  const uid = useId().replace(/:/g, '');
  const shineId = `q-pa-shine-${uid}`;
  const maskId = `q-pa-mask-${uid}`;

  return (
    <svg
      viewBox={`0 0 ${TABLE_W} ${TABLE_H}`}
      width="100%"
      style={{ maxWidth: 260, display: 'block', color: 'var(--q-pa-ink)' }}
      role="img"
      aria-label="Reorganising the table: moving, removing and adding columns"
    >
      <defs>
        <linearGradient id={shineId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="var(--q-pa-spark)" stopOpacity="0" />
          <stop offset="50%" stopColor="var(--q-pa-spark)" stopOpacity="0.5" />
          <stop offset="100%" stopColor="var(--q-pa-spark)" stopOpacity="0" />
        </linearGradient>
        {/* userSpaceOnUse so the region stays put while the band inside moves. */}
        <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width={TABLE_W} height={TABLE_H}>
          <TableContent solid />
        </mask>
      </defs>

      <TableContent />

      {/* Masked to the cells, so the sweep brightens the table's own blocks
          rather than washing over the card — a skeleton-loader shimmer. The
          mask sits on the group and the band moves inside it, because a
          transform on the masked element would drag the mask along with it. */}
      <g mask={`url(#${maskId})`}>
        <rect
          className="q-pa-shimmer"
          x="0" y="0" width="90" height={TABLE_H}
          fill={`url(#${shineId})`}
        />
      </g>
    </svg>
  );
}

/* ------------------------------------------------------------------ brain -- */

/**
 * Top-down brain. The drawing is the icon's own filled outlines (see
 * brainPaths.js) rather than strokes, so the line weight is baked into the
 * geometry — `feMorphology erode` thins it down uniformly instead of us
 * hand-editing 5 KB of path data.
 *
 * The dots ride TRACES, the real radius-56 centrelines the icon is built from.
 * They cannot use the filled outlines: a dot following one of those would run
 * up one side of a stroke and back down the other.
 */
const BRAIN_BOX = 463;
// Half the icon's 15-unit stroke, minus what the erode already takes off.
const THIN_BY = 3;

// [path, delay(s), duration(s), radius, { inset, short }] — slow enough to read
// as a signal travelling rather than something spinning. `short` swaps in the
// gentler fade the brief gyrus laps need; `inset` is the silhouette nudge below.
const TRACES = [
  [SILHOUETTE, 0, 26, 6, { inset: true }],
  [SILHOUETTE, -13, 26, 5, { inset: true }],
  [FISSURE, -1, 9, 5.5, {}],
  [GYRUS_UL, -0.5, 6.5, 5, { short: true }],
  [GYRUS_UR, -3.2, 6.5, 5, { short: true }],
  [GYRUS_LL, -4.6, 6.5, 5, { short: true }],
  [GYRUS_LR, -1.9, 6.5, 5, { short: true }],
];

// The silhouette path is the outline's *outer* edge, so a dot on it rides one
// stroke-width proud of the line. Nudging it toward the centre by a hair lands
// it on the centreline; the box is near-square, so one uniform factor does.
const INSET = `translate(${BRAIN_BOX / 2},${BRAIN_BOX / 2}) `
  + `scale(${(BRAIN_BOX / 2 - 7.5) / (BRAIN_BOX / 2)}) `
  + `translate(${-BRAIN_BOX / 2},${-BRAIN_BOX / 2})`;

const Trace = ({ d, delay, duration, r, short }) => (
  <circle
    className={short ? 'q-pa-trace q-pa-trace--short' : 'q-pa-trace'}
    r={r}
    fill="var(--q-pa-spark)"
    style={{
      offsetPath: `path("${d}")`,
      animationDelay: `${delay}s`,
      animationDuration: `${duration}s`,
    }}
  />
);

function BrainAnimation() {
  const thinId = `q-pa-thin-${useId().replace(/:/g, '')}`;

  return (
    <svg
      viewBox={`0 0 ${BRAIN_BOX} ${BRAIN_BOX}`}
      width="100%"
      style={{ maxWidth: 140, display: 'block', color: 'var(--q-pa-ink)' }}
      role="img"
      aria-label="Training models"
    >
      <defs>
        {/* sRGB, or the filter shifts the accent colour on the way through. */}
        <filter id={thinId} x="-5%" y="-5%" width="110%" height="110%"
                colorInterpolationFilters="sRGB">
          <feMorphology operator="erode" radius={THIN_BY} />
        </filter>
      </defs>

      <g className="q-pa-contour" fill="currentColor" filter={`url(#${thinId})`}>
        {SHAPES.map((d) => <path key={d.slice(0, 24)} d={d} />)}
      </g>

      {TRACES.map(([d, delay, duration, r, { inset, short }], i) => {
        const dot = <Trace d={d} delay={delay} duration={duration} r={r} short={short} />;
        return inset ? <g key={i} transform={INSET}>{dot}</g> : <React.Fragment key={i}>{dot}</React.Fragment>;
      })}
    </svg>
  );
}

/* ------------------------------------------------------------------ card -- */

const VARIANTS = {
  '2_2_preprocess': {
    Art: TableAnimation,
    title: 'Preparing your data',
    body: 'Merging, cleaning and engineering columns. This can take a few minutes — '
      + 'please keep this tab open.',
  },
  '3_model': {
    Art: BrainAnimation,
    title: 'Training with QBrain',
    body: 'Qbrain is training the best model for your data. This can take a few minutes — '
      + 'please keep this tab open.',
  },
};

export const hasPhaseActivity = (phaseId) => Boolean(VARIANTS[phaseId]);

const PhaseActivity = ({ phaseId }) => {
  const variant = VARIANTS[phaseId];
  if (!variant) return null;
  const { Art, title, body } = variant;

  return (
    <Card className="q-fade-in q-phase ui-card--flat">
      <CardContent className="q-phase__body">
        <div style={{ flexShrink: 0, display: 'grid', placeItems: 'center' }}>
          <Art />
        </div>
        <div className="q-phase__copy">
          <div className="q-phase__title">{title}</div>
          <div className="q-phase__body-text">{body}</div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PhaseActivity;
